import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import { organisationApi, UNAVAILABLE } from '@/api/organisation';
import type { InstitutionProfile, InstitutionUser, Campus } from '@/types';
import type {
  AcademicRank,
  EducationSubsystem,
  InstitutionPositionView,
  InstitutionTemplate,
  RoleBundle,
} from '@/types/organisation';
import { TERMINOLOGY_KEYS } from '@/types/organisation';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Icon } from '@/components/icons';
import { PositionsTab } from './organisation/PositionsTab';
import { AppointmentsTab } from './organisation/AppointmentsTab';
import { RanksTab } from './organisation/RanksTab';
import styles from './AdminOrganisationPage.module.css';

/**
 * Organisation and Roles.
 *
 * The console used to show one list called "roles" holding Associate Professor, Dean,
 * Librarian and Exam Officer side by side. Those are three different kinds of thing and
 * only one of them grants any permission. This page keeps them apart, one tab each, in
 * the order an administrator meets them: what offices exist here, who holds what, what
 * ranks exist, and what actually confers access.
 *
 * TWO RULES RUN THROUGH IT.
 *
 * The canonical code is never hidden. Every position shows its code beside its title,
 * because the title is a label an institution may change and the code is what
 * authorization is written against. Renaming Principal to Head of School must be
 * visibly a renaming, not an edit to something that decides access.
 *
 * Nothing is mocked. The writes on this page — enable/disable, rename, the
 * one-transaction appointment — are wired to the institution-scoped identity endpoints
 * that now serve them. The one capability still unserved (a terminology override)
 * renders disabled and names the endpoint it is waiting for; see UNAVAILABLE.
 */

/** i18n prefix, kept in one place — it appears on well over a hundred keys. */
const NS = 'admin.organisation';

const TABS = ['overview', 'positions', 'appointments', 'ranks', 'bundles'] as const;

type TabId = (typeof TABS)[number];

export function AdminOrganisationPage() {
  const { t, i18n } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';
  const locale = (i18n.language || 'en').split('-')[0];

  const [tab, setTab] = useState<TabId>('overview');
  const tabRefs = useRef(new Map<TabId, HTMLButtonElement>());

  /* ─── The institution's own configuration, which everything else is resolved from ─── */

  const { state: profileState, reload: reloadProfile } = useAsyncData<InstitutionProfile>(
    `org-profile-${institutionId}`,
    () => api.admin.profile.get(institutionId),
    !!institutionId,
  );

  // api.admin.profile.get swallows its own failure and resolves null, so a ready state
  // is not proof of a profile.
  const profile = profileState.status === 'ready' ? profileState.data : null;
  const institutionType = profile?.institutionType ?? '';

  const { state: templateState, reload: reloadTemplate } = useAsyncData<InstitutionTemplate>(
    `org-template-${institutionType}`,
    () => organisationApi.template.resolve(institutionType),
    !!institutionType,
  );

  const template = templateState.status === 'ready' ? templateState.data : null;
  const subsystem: EducationSubsystem = template?.educationSubsystem ?? 'GENERAL';

  /**
   * The office catalogue AS THIS INSTITUTION SEES IT — the per-institution view that
   * carries enablement and title overrides, not the shared per-type catalogue.
   *
   * Keyed on locale and subsystem so switching language re-reads the titles from the
   * server rather than leaving French titles on an English page.
   */
  const { state: positionsState, reload: reloadPositions } = useAsyncData<InstitutionPositionView[]>(
    `org-inst-positions-${institutionId}-${locale}-${subsystem}`,
    () => organisationApi.positions.forInstitution(institutionId, locale, subsystem),
    !!institutionId,
  );

  const { state: ranksState, reload: reloadRanks } = useAsyncData<AcademicRank[]>(
    'org-ranks',
    () => organisationApi.ranks.list(),
    true,
  );

  const { state: catalogueState, reload: reloadCatalogue } = useAsyncData<RoleBundle[]>(
    'org-bundle-catalogue',
    () => organisationApi.bundles.catalogue(),
    true,
  );

  const { state: institutionBundlesState, reload: reloadInstitutionBundles } = useAsyncData<RoleBundle[]>(
    `org-bundles-${institutionId}`,
    () => organisationApi.bundles.forInstitution(institutionId),
    !!institutionId,
  );

  const { state: usersState } = useAsyncData<InstitutionUser[]>(
    `admin-users-${institutionId}`,
    () => api.admin.users.list(institutionId),
    !!institutionId,
  );

  const { state: campusesState } = useAsyncData<Campus[]>(
    `admin-campuses-${institutionId}`,
    () => api.admin.campuses.list(institutionId),
    !!institutionId,
  );

  const users = usersState.status === 'ready' ? usersState.data : [];
  const campuses = campusesState.status === 'ready' ? campusesState.data : [];
  const ranks = ranksState.status === 'ready' ? ranksState.data : [];

  /** Institution-composed bundles layered over the platform catalogue, deduplicated. */
  const allBundles = useMemo(() => {
    const merged = new Map<string, RoleBundle>();
    if (catalogueState.status === 'ready') for (const b of catalogueState.data) merged.set(b.id, b);
    if (institutionBundlesState.status === 'ready') for (const b of institutionBundlesState.data) merged.set(b.id, b);
    return [...merged.values()];
  }, [catalogueState, institutionBundlesState]);

  /* ─── The tab bar. Roving tabindex, arrows move, Home/End jump — WAI-ARIA tabs. ─── */

  const focusTab = (next: TabId) => {
    setTab(next);
    tabRefs.current.get(next)?.focus();
  };

  const moveFocus = (from: TabId, delta: number) => {
    const index = TABS.indexOf(from);
    focusTab(TABS[(index + delta + TABS.length) % TABS.length]);
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.headerName}>{t(`${NS}.title`)}</h1>
          <p className={styles.headerSubtitle}>{t(`${NS}.subtitle`)}</p>
        </div>
      </header>

      <div role="tablist" aria-label={t(`${NS}.title`)} className={styles.tabList}>
        {TABS.map((id) => (
          <button
            key={id}
            ref={(node) => {
              if (node) tabRefs.current.set(id, node);
              else tabRefs.current.delete(id);
            }}
            type="button"
            role="tab"
            id={`org-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`org-panel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            className={tab === id ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => setTab(id)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                moveFocus(id, 1);
              } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                moveFocus(id, -1);
              } else if (event.key === 'Home') {
                event.preventDefault();
                focusTab(TABS[0]);
              } else if (event.key === 'End') {
                event.preventDefault();
                focusTab(TABS[TABS.length - 1]);
              }
            }}
          >
            {t(`${NS}.tabs.${id}`)}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`org-panel-${tab}`}
        aria-labelledby={`org-tab-${tab}`}
        tabIndex={0}
        className={styles.tabPanel}
      >
        {tab === 'overview' && (
          <OverviewTab
            institutionType={institutionType}
            profileReady={profileState.status === 'ready'}
            templateState={templateState}
            reloadTemplate={reloadTemplate}
            template={template}
          />
        )}

        {tab === 'positions' &&
          (!institutionType && profileState.status === 'ready' ? (
            <Card>
              <Unavailable title={t(`${NS}.terminology.noType`)} body={t(`${NS}.positions.noTypeBody`)} />
            </Card>
          ) : (
            <PositionsTab
              institutionId={institutionId}
              locale={locale}
              subsystem={subsystem}
              institutionType={institutionType}
              profileMissing={profileState.status === 'ready' && !profile}
              state={positionsState}
              onRetry={reloadPositions}
              onChanged={reloadPositions}
            />
          ))}

        {tab === 'appointments' && (
          <AppointmentsTab
            institutionId={institutionId}
            locale={locale}
            subsystem={subsystem}
            positionsState={positionsState}
            users={users}
            campuses={campuses}
            ranks={ranks}
            bundles={allBundles}
          />
        )}

        {tab === 'ranks' && <RanksTab state={ranksState} onRetry={reloadRanks} locale={locale} />}

        {tab === 'bundles' && (
          <section className={styles.section}>
            <Card className={styles.groupCard}>
              <header className={styles.groupHeader}>
                <h3 className={styles.groupTitle}>{t(`${NS}.bundles.institutionTitle`)}</h3>
              </header>
              <AsyncPanel
                state={institutionBundlesState}
                onRetry={reloadInstitutionBundles}
                isEmpty={(data) => data.length === 0}
                emptyTitle={t(`${NS}.bundles.institutionEmpty`)}
                emptyBody={t(`${NS}.bundles.institutionEmptyBody`)}
              >
                {(data) => <BundleList bundles={data} />}
              </AsyncPanel>
            </Card>

            <Card className={styles.groupCard}>
              <header className={styles.groupHeader}>
                <h3 className={styles.groupTitle}>{t(`${NS}.bundles.catalogueTitle`)}</h3>
              </header>
              <AsyncPanel
                state={catalogueState}
                onRetry={reloadCatalogue}
                isEmpty={(data) => data.length === 0}
                emptyTitle={t(`${NS}.bundles.catalogueEmpty`)}
                emptyBody={t(`${NS}.bundles.catalogueEmptyBody`)}
              >
                {(data) => <BundleList bundles={data} />}
              </AsyncPanel>
            </Card>
          </section>
        )}
      </div>

      {profileState.status === 'ready' && !profile && (
        <Card>
          <Unavailable title={t(`${NS}.noProfile`)} body={t(`${NS}.noProfileBody`)} />
          <button type="button" className={styles.ghostAction} onClick={reloadProfile}>
            {t('common.retry')}
          </button>
        </Card>
      )}
    </div>
  );
}

/** The model explainer and the institution's vocabulary — the orientation tab. */
function OverviewTab({
  institutionType,
  profileReady,
  templateState,
  reloadTemplate,
  template,
}: {
  institutionType: string;
  profileReady: boolean;
  templateState: ReturnType<typeof useAsyncData<InstitutionTemplate>>['state'];
  reloadTemplate: () => void;
  template: InstitutionTemplate | null;
}) {
  const { t } = useTranslation();

  const resolvedTerms = template
    ? TERMINOLOGY_KEYS.flatMap((key) => {
        const term = template.terminology[key];
        return term ? [{ key, term }] : [];
      })
    : [];

  return (
    <>
      <Card className={styles.explainer}>
        <p className={styles.explainerLead}>{t(`${NS}.model.lead`)}</p>
        <ul className={styles.explainerList}>
          <li>
            <strong>{t(`${NS}.model.positionTerm`)}</strong> {t(`${NS}.model.position`)}
          </li>
          <li>
            <strong>{t(`${NS}.model.rankTerm`)}</strong> {t(`${NS}.model.rank`)}
          </li>
          <li>
            <strong>{t(`${NS}.model.bundleTerm`)}</strong> {t(`${NS}.model.bundle`)}
          </li>
          <li>
            <strong>{t(`${NS}.model.appointmentTerm`)}</strong> {t(`${NS}.model.appointment`)}
          </li>
        </ul>
      </Card>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon} aria-hidden="true">
            <Icon name="institution" size={18} />
          </span>
          <div>
            <h2 className={styles.sectionTitle}>{t(`${NS}.terminology.title`)}</h2>
            <p className={styles.sectionBody}>{t(`${NS}.terminology.subtitle`)}</p>
          </div>
        </div>

        <Card>
          {!institutionType && profileReady ? (
            <Unavailable title={t(`${NS}.terminology.noType`)} body={t(`${NS}.terminology.noTypeBody`)} />
          ) : (
            <AsyncPanel
              state={templateState}
              onRetry={reloadTemplate}
              isEmpty={(data) => Object.keys(data.terminology).length === 0}
              emptyTitle={t(`${NS}.terminology.empty`)}
              emptyBody={t(`${NS}.terminology.emptyBody`)}
            >
              {(data) => (
                <>
                  <p className={styles.sourceNote}>
                    {t(`${NS}.terminology.source`, {
                      type: t(`admin.profile.institutionTypes.${data.institutionType}`, {
                        defaultValue: data.institutionType,
                      }),
                      subsystem: t(`${NS}.subsystems.${data.educationSubsystem}`, {
                        defaultValue: data.educationSubsystem,
                      }),
                    })}
                  </p>
                  <dl className={styles.terms}>
                    {resolvedTerms.map(({ key, term }) => (
                      <div key={key} className={styles.term}>
                        <dt className={styles.termKey}>{t(`${NS}.terminology.keys.${key}`)}</dt>
                        <dd className={styles.termValue}>{term}</dd>
                      </div>
                    ))}
                  </dl>
                  <Unavailable
                    title={t(`${NS}.terminology.overrideTitle`)}
                    body={t(`${NS}.terminology.overrideBody`)}
                    endpoints={[UNAVAILABLE.terminologyOverride]}
                  />
                </>
              )}
            </AsyncPanel>
          )}
        </Card>
      </section>
    </>
  );
}

function BundleList({ bundles }: { bundles: RoleBundle[] }) {
  const { t } = useTranslation();
  return (
    <ul className={styles.bundleList}>
      {bundles.map((bundle) => (
        <li key={bundle.id} className={styles.bundleRow}>
          <div className={styles.positionMain}>
            <span className={styles.positionTitle}>{bundle.displayName}</span>
            <code className={styles.canonicalCode}>{bundle.code}</code>
            {bundle.requiresApproval && <Badge tone="warning">{t(`${NS}.positions.requiresApproval`)}</Badge>}
            {bundle.systemManaged && <Badge tone="info">{t(`${NS}.positions.systemManaged`)}</Badge>}
            {!bundle.active && <Badge tone="neutral">{t(`${NS}.positions.inactive`)}</Badge>}
          </div>
          {bundle.description && <p className={styles.positionDescription}>{bundle.description}</p>}
          {bundle.scopeRequirement && (
            <div className={styles.positionMeta}>
              <span>
                {t(`${NS}.bundles.scopeRequirement`)}:{' '}
                {t(`${NS}.scopeRequirements.${bundle.scopeRequirement}`, {
                  defaultValue: bundle.scopeRequirement,
                })}
              </span>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * A capability that has no endpoint behind it.
 *
 * The endpoint is printed rather than described. Whoever is looking at a disabled
 * control needs to be able to hand somebody an exact route, and "this feature is coming
 * soon" has never helped anybody file a ticket.
 */
function Unavailable({
  title,
  body,
  endpoints,
}: {
  title: string;
  body: string;
  endpoints?: readonly string[];
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.unavailable} role="note">
      <span className={styles.unavailableIcon} aria-hidden="true">
        <Icon name="info" size={18} />
      </span>
      <div>
        <p className={styles.unavailableTitle}>{title}</p>
        <p className={styles.unavailableBody}>{body}</p>
        {endpoints && endpoints.length > 0 && (
          <details className={styles.unavailableDetails}>
            <summary>{t(`${NS}.endpointsNeeded`)}</summary>
            <ul>
              {endpoints.map((endpoint) => (
                <li key={endpoint}>
                  <code>{endpoint}</code>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}
